import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, tap} from 'rxjs';
import {ServicePackage, UserSubscription} from '../interfaces/packages.interface';
import {PackageResource} from '../resource/package.resource';
import {UserService} from './user.service';
import {isInIframe} from '../../shared/helpers/maporium.validators';

@Injectable({
  providedIn: 'root'
})
export class PackageService {
  private userPackage$: BehaviorSubject<UserSubscription | null> = new BehaviorSubject<UserSubscription | null>(null);

  constructor(private packageResource: PackageResource,
              private userService: UserService) {
  }

  init(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (isInIframe()) {
        resolve();
        return;
      }
      const token = localStorage.getItem('accessToken');

      if (!token) {
        resolve();
        return;
      }

      this.loadUserPackage().subscribe(
        () => resolve(),
        err => {
          console.error('Error initializing user package', err);
          resolve();  // Still resolve, so the app continues to load, but handle/log the error appropriately
        }
      );
    });
  }

  userPackageStream(): Observable<UserSubscription | null> {
    return this.userPackage$.asObservable();
  }

  getAllPublicPackages(): Observable<ServicePackage[]> {
    return this.packageResource.getAllPublicPackages();
  }

  getOne(packageId: string) {
    return this.packageResource.getOne(packageId);
  }

  // Call this method after user logs in, to populate userPackage data
  loadUserPackage(): Observable<UserSubscription> {
    if (isInIframe()) {
      return of(null as any);
    }
    return this.packageResource.getUserPackage()
      .pipe(
        tap(userPackage => {
          this.userPackage$.next(userPackage);
          const currentLocalUser = this.userService.getCurrentUserFromStorage();
          if (currentLocalUser) {
            currentLocalUser.subscription = userPackage;
            // Persist — assigning to the parsed copy alone was a no-op
            this.userService.setUser(currentLocalUser);
          }
        })
      );
  }

  // Utility method to check if a user has a specific module
  hasModule(moduleName: string): boolean {
    const currentLocalUser = this.userService.getCurrentUserFromStorage();
    if (!currentLocalUser || !currentLocalUser.subscription) {
      return false;
    }
    return currentLocalUser.subscription.package.modules.some(module => module.name.toLowerCase() === moduleName.toLowerCase());
  }

  hasModules(moduleNames: string[]): boolean {
    const currentLocalUser = this.userService.getCurrentUserFromStorage();
    if (!currentLocalUser || !currentLocalUser.subscription) {
      return false;
    }
    return moduleNames.every((moduleName) => {
      if (!currentLocalUser.subscription)  {
        return false;
      }
      return currentLocalUser.subscription.package.modules.some(module => module.name.toLowerCase() === moduleName.toLowerCase())
    });
  }

  hasPackage(packageName: string): boolean {
    const currentLocalUser = this.userService.getCurrentUserFromStorage();
    if (!currentLocalUser || !currentLocalUser.subscription) {
      return false;
    }
    return currentLocalUser.subscription.package.name.toLowerCase() === packageName.toLowerCase();
  }

  hasPackages(packageNames: string[]): boolean {
    const currentLocalUser = this.userService.getCurrentUserFromStorage();
    if (!currentLocalUser || !currentLocalUser.subscription) {
      return false;
    }
    return packageNames.every((packageName) => {
      // @ts-ignore
      return currentLocalUser.subscription.package.name.toLowerCase() === packageName.toLowerCase();
    });
  }

  isDev(): boolean {
    return this.hasPackage('Developer');
  }

  // "Publisher or Tester" gate (Tester = Developer plan).
  canManagePublisherName(): boolean {
    return (
      this.hasPackage('Publisher') ||
      this.hasPackage('Developer')
    );
  }

  isOnTestPlan(): boolean {
    const currentLocalUser = this.userService.getCurrentUserFromStorage();
    return !!currentLocalUser?.subscription?.package?.isTestPlan;
  }

  isDefaulter(): boolean {
    const user = this.userService.getCurrentUserFromStorage();
    if (user?.status === 'limited') return true;
    // Detect locally-expired subscriptions before backend syncs
    const sub = user?.subscription;
    if (sub?.expirationDate && new Date(sub.expirationDate) <= new Date() && !sub.isTrial) {
      return true;
    }
    return false;
  }
}
